import { GetServerSideProps, NextPage } from "next";
import Featurette from "../../components/Featurette";
import { CARD_IMAGE_PLACEMENTS, ctaClickSources, FEATURETTE_IMAGE_PLACEMENTS } from "../../lib/constants";
import FeaturetteTextContent from "../../components/Featurette/FeaturetteTextContent";
import styles from '../../styles/Blog.module.css'
import { useEffect, useMemo, useState } from "react";
import { AgeGroupData, ArticleDTO, AuthorDTO, CategoryDTO, Locale } from "../../lib/types";
import ArticleRecommendation from "../../components/ArticleRecommendation";
import Card from "../../components/Card";
import Controls from "../../components/Blog/Controls";
import BlogEmptyState from "../../components/Blog/BlogEmptyState";
import Pagination from "../../components/Blog/Pagination";
import qs from "qs";
import { mapArticleData, mapAuthorData, mapCategoryData } from "../../lib/utils/mappers";
import { mapAgeGroupData } from '../../lib/utils/mappers/map-age-group-data';
import api from "../../lib/utils/api";
import { useRouter } from 'next/router'
import { useAppContext } from "../../context/shared";
import { Col, Container, Row } from "react-bootstrap";
import cmsEndpoints from "../../lib/utils/cmsEndpoints";
import { PageHead } from "../../components/PageHead";
import { useDispatch } from "react-redux";
import { clickedCTA } from "../../store/actions/events";


const Blog: NextPage = (props: any) => {
  const router = useRouter()
  const dispatch = useDispatch()
  const { localization } = useAppContext()
  const [selectedControls, setSelectedControls] = useState({
    ageGroups: [],
    categories: [],
    authors: [],
    sort: 'newest'
  })

  const {
    count,
    selectedPage,
    pages,
  } = useMemo(() => {
    const appliedFilters: any = selectedControls.categories?.length || selectedControls.ageGroups?.length || selectedControls.authors?.length
    const adjustment = selectedControls.sort === "oldest" || appliedFilters ? 0 : 1
    const count = props.articlesPagination.total - adjustment
    return {
      count,
      selectedPage: Math.floor((props.articlesPagination.start - adjustment) / props.articlesPagination.limit) + 1,
      pages: Math.ceil(count / props.articlesPagination.limit)
    }
  }, [props.articlesPagination, selectedControls])

  useEffect(() => {
    const [_, query] = window.location.href.split("?")
    const currentQuery = qs.parse(query)
    const nextSelectedControls: any = {
      sort: currentQuery.sort || "newest",
      ageGroups: currentQuery.ageGroups || [],
      categories: currentQuery.categories || [],
      authors: currentQuery.authors || [],
    }
    setSelectedControls(nextSelectedControls)
  }, [router])

  const handlePageClick = (nextPage: number) => {
    const [url, query] = window.location.href.split("?")
    const nextQuery = qs.parse(query || "")
    nextQuery.page = nextPage.toString()
    router.push([url, qs.stringify(nextQuery)].join("?"))
  }

  const handleControlsChange = (nextSelectedControls: any) => {
    const [url, query] = window.location.href.split("?")
    const prevQuery = qs.parse(query || "")
    const nextQuery = Object.assign({}, { ...(prevQuery.page ? { page: prevQuery.page || 1 } : {}) }, nextSelectedControls)
    router.push([url, qs.stringify(nextQuery)].join("?"), undefined, { scroll: false })
  }

  return (
    <>
      <PageHead title='Blog' />
      <div>
        {
          props.featuredArticle &&
          <Featurette
            image={props.featuredArticle.mainImage}
            imagePlacement={FEATURETTE_IMAGE_PLACEMENTS.RIGHT}
            backgroundColor='#FAFCFC'
            mobileFullscreenImage={true}
          >
            <div className='h-100 w-100 d-flex align-items-center justify-content-center justify-content-md-start'>
              <FeaturetteTextContent
                subtitle={localization.blog_last_article}
                title={props.featuredArticle.title}
                body={props.featuredArticle.intro}
                buttonText={localization.blog_read_more_btn}
                link={"/blog/" + props.featuredArticle.slug}
                onCTAClick={() => dispatch(clickedCTA({ name: ctaClickSources.blog.readMore.name, url: router.asPath, blogPostTitle: props.featuredArticle.title }))}
              />
            </div>
          </Featurette>
        }
        <Controls
          count={count}
          ageGroups={props.filters.ageGroups}
          categories={props.filters.categories}
          authors={props.filters.authors}
          selectedControls={selectedControls}
          onControlChange={handleControlsChange}
        />
        <Container>
          {props.articles.length > 0 &&
            <Row className={styles.articles}>
              {props.articles.map((article: ArticleDTO, index: number) => (
                <Col key={index} sm='12' md='6' lg='4' className="mb-4" >
                  <Card
                    title={article.title}
                    body={article.intro}
                    tags={article.categories}
                    buttonText={localization.blog_read_more_btn}
                    image={article.mainImage}
                    imagePlacement={CARD_IMAGE_PLACEMENTS.TOP_FULL}
                    link={"/blog/" + article.slug}
                  />
                </Col>
              ))}
            </Row>
          }
          {props.articles.length === 0 && (
            <>
              <BlogEmptyState />
              <hr style={{ margin: '0 5rem' }} />
              <ArticleRecommendation title={localization.blog_popular_articles_list}>
                {props.popularArticles.map((article: ArticleDTO, index: number) => (
                  <Col sm='12' md='6' lg='4' className="mb-4" key={index}>
                    <Card
                      key={index}
                      title={article.title}
                      body={article.intro}
                      buttonText={localization.blog_read_more_btn}
                      image={article.mainImage}
                      imagePlacement={CARD_IMAGE_PLACEMENTS.TOP_FULL}
                      link={"/blog/" + article.slug}
                    />
                  </Col>
                ))}
              </ArticleRecommendation>
            </>
          )}
          <div className='pb-5'>
            <Pagination page={selectedPage} pages={pages} onPageClick={(nextPage: number) => handlePageClick(nextPage)} />
          </div>
        </Container>
      </div>
    </>
  )
}





export const getServerSideProps: GetServerSideProps = async (context) => {
  context.res.setHeader(
    'Cache-Control',
    'public, s-maxage=10, stale-while-revalidate=59'
  )
  const [url, queryString] = (context?.req?.url || "").split("?")
  const query = qs.parse(queryString || "")

  const sort = query.sort === "oldest" ? "asc" : "desc"
  const appliedFilters: any = query.categories?.length || query.ageGroups?.length || query.authors?.length
  const adjustment = query.sort === "oldest" || appliedFilters ? 0 : 1
  const limit = 6
  const offset = ((parseInt(query.page as string || "1") - 1) * limit) + adjustment

  const [ageGroupsData, categoriesData, authorsData, articlesData, featuredArticleData] = await Promise.all([
    api.get(cmsEndpoints.ageGroups()),
    api.get(cmsEndpoints.blogPostCategories()),
    api.get(cmsEndpoints.authors()),
    api.get(cmsEndpoints.blogPost({
      sort,
      offset,
      limit,
      categories: query.categories as string[],
      authors: query.authors as string[],
      ageGroups: query.ageGroups as string[]
    })),
    api.get(cmsEndpoints.featuredBlogPost())
  ])

  const ageGroups: AgeGroupData = (ageGroupsData?.data?.data || []).map((ageGroup: any) => {
    return mapAgeGroupData(ageGroup, context.locale as Locale)
  })
  const categories: CategoryDTO = (categoriesData?.data?.data || []).map((category: any) => {
    return mapCategoryData(category, context.locale as Locale)
  })
  const authors: AuthorDTO = (authorsData?.data?.data || []).map((author: any) => {
    return mapAuthorData(author, context.locale as Locale)
  })
  const filters = { ageGroups, categories, authors }
  const articles: ArticleDTO[] = (articlesData?.data?.data || []).map((article: any) => {
    return mapArticleData(article.attributes, context.locale as Locale)
  })

  const featuredArticle: ArticleDTO = mapArticleData(featuredArticleData?.data?.data?.[0]?.attributes, context.locale as Locale)

  const popularArticlesData = articles.length ? {} as any : await api.get(cmsEndpoints.popularBlogPosts())
  const popularArticles: ArticleDTO[] = (popularArticlesData?.data?.data || []).map((article: any) => {
    return mapArticleData(article.attributes, context.locale as Locale)
  })

  return {
    props: {
      articlesPagination: articlesData?.data?.meta?.pagination || {},
      articles,
      featuredArticle,
      popularArticles,
      filters
    }
  }
}


export default Blog
